import { useEffect, useRef, useState } from "react";
import { cropImageAroundBbox } from "utils/cropImageAroundBbox";
import type { UseGridTileImage } from "./types";

// 240px source for ~120px cells keeps thumbnails crisp on retina screens.
const TILE_IMAGE_SIZE_PX = 240;
const TILE_PAINT_ROOT_MARGIN = "400px";

export const useGridTileImage: UseGridTileImage = ({
  imageUrl,
  bbox,
  pageScaleFactor,
  sourceWidthPx,
  sourceHeightPx,
  loadPageImage,
}) => {
  const tileRef = useRef<HTMLDivElement | null>(null);

  const [tileImageDataUrl, setTileImageDataUrl] = useState<string | null>(null);
  const [hasTileImageFailed, setHasTileImageFailed] = useState(false);

  useEffect(() => {
    const tileNode = tileRef.current;
    if (!tileNode) {
      return;
    }

    let cancelled = false;

    const paintTile = async () => {
      try {
        const sourceImage = await loadPageImage(imageUrl);
        if (cancelled) {
          return;
        }

        const dataUrl = cropImageAroundBbox({
          sourceImage,
          bbox: {
            x1: bbox.x1 * pageScaleFactor,
            y1: bbox.y1 * pageScaleFactor,
            x2: bbox.x2 * pageScaleFactor,
            y2: bbox.y2 * pageScaleFactor,
          },
          sourceWidthPx,
          sourceHeightPx,
          outputSizePx: TILE_IMAGE_SIZE_PX,
        });

        if (cancelled) {
          return;
        }
        if (dataUrl === "") {
          setHasTileImageFailed(true);
        } else {
          setTileImageDataUrl(dataUrl);
        }
      } catch {
        if (!cancelled) {
          setHasTileImageFailed(true);
        }
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }
        observer.disconnect();
        void paintTile();
      },
      { rootMargin: TILE_PAINT_ROOT_MARGIN },
    );
    observer.observe(tileNode);

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [
    imageUrl,
    bbox,
    pageScaleFactor,
    sourceWidthPx,
    sourceHeightPx,
    loadPageImage,
  ]);

  return { tileRef, tileImageDataUrl, hasTileImageFailed };
};
